/**
 * 000 Onboarding form schema — single source of truth for the partner portal
 * form and the internal review screen. Mirrors the IMT "COUNTERPARTY
 * ON-BOARDING FORM – FOR A CORPORATE ENTITY" document field-for-field
 * (see feature_planning/KYC_PARTNER_ONBOARDING/01_REQUIREMENTS.md §B).
 *
 * form_data shape: { [section.key]: {field: value} | [{col: value}, ...] }
 */

import {
    CREDITOR_DEBTOR_OPTIONS,
    INDUSTRY_OPTIONS,
    KYC_PARTNER_TYPE_OPTIONS,
    coerceToVocabulary,
} from '../../constants/crmVocabularies';

export const KYC_FORM_SECTIONS = [
    {
        key: 'company',
        title: 'Company Identity',
        type: 'fields',
        fields: [
            { key: 'counterparty_full_name', label: 'Counterparty Full Name', required: true },
            { key: 'trading_name', label: 'Trading Name (if different)' },
            { key: 'website', label: 'Company Website' },
            { key: 'company_email', label: 'Company Email' },
            { key: 'company_phone', label: 'Company Phone' },
        ],
    },
    {
        key: 'classification',
        title: 'Partner Classification',
        type: 'fields',
        fields: [
            // Tickbox groups, NOT free text: the CRM partner profile stores
            // these three as controlled lists, so typed answers could never
            // cross that seam. The old placeholders ("e.g. Supplier, Customer"
            // / "Shipping, Logistics, Mining") named five values that exist in
            // neither CRM list.
            {
                key: 'partner_type',
                label: 'Type of Partner',
                type: 'multiselect',
                options: KYC_PARTNER_TYPE_OPTIONS,
                help: 'Tick every type that applies.',
            },
            {
                key: 'industries',
                label: 'Industries',
                type: 'multiselect',
                options: INDUSTRY_OPTIONS,
                help: 'Tick every industry you operate in.',
            },
            {
                key: 'creditor_debtor',
                label: 'Creditor / Debtor',
                type: 'multiselect',
                options: CREDITOR_DEBTOR_OPTIONS,
                help: 'Tick both if you will both invoice IMT and be invoiced by IMT.',
            },
        ],
    },
    {
        key: 'corporate',
        title: 'Corporate Details',
        type: 'fields',
        fields: [
            { key: 'established', label: 'When was the Company established' },
            { key: 'country_of_incorporation', label: 'Country of Incorporation', required: true },
            { key: 'registration_number', label: 'Official Corporate Registration No', required: true },
            { key: 'vat_number', label: 'VAT Number (or W9 / Taxpayer ID)' },
            { key: 'lei', label: 'Legal Entity Identifier (LEI), if any' },
        ],
    },
    {
        key: 'commodities',
        title: 'Traded Commodities',
        type: 'fields',
        fields: [
            { key: 'traded_commodities', label: 'Primary commodities you trade / would trade with IMT', multiline: true },
        ],
    },
    {
        key: 'ubo',
        title: 'Ultimate Beneficial Ownership',
        help: 'Parent company and/or majority shareholders (10% or more) and/or those who otherwise directly or indirectly control the Company.',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Name of Entity or Person' },
            { key: 'holding_percent', label: '% Holding' },
            { key: 'country', label: 'Country of Incorporation / Residence' },
        ],
    },
    {
        key: 'groups',
        title: 'Group of Companies',
        help: 'Indicate if the Company belongs to any group of companies anywhere.',
        type: 'table',
        maxRows: 6,
        columns: [
            { key: 'group_name', label: 'Name of Group' },
            { key: 'holding_percent', label: '% Holding' },
            { key: 'hq_country', label: 'Country where Group is Headquartered' },
        ],
        extraFields: [
            { key: 'has_organogram', label: 'Do you have a Group Organogram?', type: 'yesno', help: 'If yes, kindly attach it under "Other Supporting Document" in the Documents step.' },
        ],
    },
    {
        key: 'directors',
        title: 'Directors & Key Managers',
        help: 'Names of all Directors of the Company, and TWO key managers if not directors.',
        type: 'table',
        maxRows: 6,
        columns: [
            { key: 'name', label: 'Name' },
            { key: 'title', label: 'Title or Position' },
        ],
    },
    {
        key: 'addresses',
        title: 'Company Address',
        type: 'fields',
        fields: [
            { key: 'registered_address', label: 'Full Registered Address', multiline: true, required: true },
            { key: 'trading_address', label: 'Trading Address', multiline: true },
            { key: 'invoicing_address', label: 'Invoicing Address', multiline: true },
            { key: 'invoicing_explanation', label: 'Brief explanation if invoicing address differs from trading address', multiline: true },
        ],
    },
    {
        key: 'contacts',
        title: 'Individual Contact Details',
        help: 'Your day-to-day contacts for IMT.',
        type: 'fixed-table',
        rows: [
            { key: 'trader', label: 'Trader' },
            { key: 'finance', label: 'Invoicing / Finance' },
            { key: 'bank_verification', label: 'Bank Verification' },
            { key: 'other', label: 'Other' },
        ],
        columns: [
            { key: 'name', label: 'Name' },
            { key: 'phone', label: 'Tel / Mobile' },
            { key: 'email', label: 'Email' },
        ],
    },
    {
        key: 'banking',
        title: 'Banking Information (Primary Account)',
        help: 'The beneficiary name must be the same as the counterparty name. If not, a letter confirming the relationship must be provided. For vendors, bank information must be provided on bank letterhead.',
        type: 'fields',
        fields: [
            { key: 'account_name', label: 'Bank Account Name', required: true },
            { key: 'bank_name', label: 'Name of Bank', required: true },
            { key: 'bank_address', label: 'Bank Address' },
            { key: 'bank_country', label: 'Country of Bank Account' },
            { key: 'account_number', label: 'Bank Account Number' },
            { key: 'currency', label: 'Bank Account Currency' },
            { key: 'iban_swift', label: 'Bank IBAN or SWIFT' },
            { key: 'account_manager_contact', label: 'Bank Account Manager Contact (optional)' },
        ],
    },
    {
        key: 'banking_secondary',
        title: 'Secondary Bank Account (optional)',
        type: 'fields',
        fields: [
            { key: 'account_name', label: 'Bank Account Name' },
            { key: 'bank_name', label: 'Name of Bank' },
            { key: 'bank_address', label: 'Bank Address' },
            { key: 'bank_country', label: 'Country of Bank Account' },
            { key: 'account_number', label: 'Bank Account Number' },
            { key: 'currency', label: 'Bank Account Currency' },
            { key: 'iban_swift', label: 'Bank IBAN or SWIFT' },
        ],
    },
    {
        key: 'care_of',
        title: '"Care Of" Company (if applicable)',
        help: 'Only if a vessel management company or operator purchases on behalf of your company. Note: the care-of company must be onboarded with IMT separately.',
        type: 'fields',
        fields: [
            { key: 'care_of_name', label: 'Care-of company name (vessel manager / operator buying on your behalf)' },
            { key: 'care_of_relationship', label: 'Relationship to the care-of company' },
        ],
    },
    {
        key: 'signatory',
        title: 'Authorized Signatory & Declaration',
        help: 'I hereby confirm that the details given above are true and correct to the best of my knowledge.',
        type: 'fields',
        fields: [
            { key: 'name', label: 'Full Name' },
            { key: 'position', label: 'Position / Title' },
            { key: 'email', label: 'Email' },
            { key: 'location', label: 'Location' },
            { key: 'date', label: 'Date' },
        ],
    },
];

/**
 * KYC South Africa variant — additional sections (ClickUp requirement #7):
 * BBBEE, products/tonnage, financial & trade references, mine/processing
 * sites, stockpile, and the compliance questionnaire. Mirrors
 * backend/kyc/form_schema.py SA_SECTIONS — keys must match exactly.
 */
export const KYC_SA_SECTIONS = [
    {
        key: 'sa_corporate_extra',
        title: 'Corporate Details (South Africa)',
        type: 'fields',
        fields: [
            { key: 'company_type', label: 'Company Type' },
            { key: 'related_companies', label: 'Related Companies (Ownership, Subsidiaries, Partnership, Holding Company)', multiline: true },
            { key: 'postal_address', label: 'Postal Address', multiline: true },
            { key: 'principal_place_of_business', label: 'Principal Place of Business' },
        ],
    },
    {
        key: 'sa_bbbee',
        title: 'BBBEE (South Africa)',
        type: 'fields',
        fields: [
            { key: 'bbbee_rating', label: 'BBBEE Rating' },
            { key: 'bbbee_last_assessment', label: 'Date of last BBBEE assessment' },
        ],
    },
    {
        key: 'sa_individual_owners',
        title: 'Ownership — Individuals',
        help: 'Every individual shareholder, with passport/ID and ownership percentage.',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Full Name' },
            { key: 'nationality', label: 'Nationality' },
            { key: 'dob', label: 'Date of Birth' },
            { key: 'address', label: 'Address' },
            { key: 'passport_id', label: 'Passport or ID Number' },
            { key: 'percent', label: 'Ownership %' },
        ],
    },
    {
        key: 'sa_corporate_shareholders',
        title: 'Ownership — Corporate Shareholders',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Company Name / Legal Entity' },
            { key: 'incorporation_date', label: 'Date of Incorporation' },
            { key: 'country', label: 'Country of Incorporation' },
            { key: 'percent', label: '% Ownership' },
        ],
    },
    {
        key: 'sa_pep',
        title: 'Politically Exposed Persons (PEP)',
        type: 'fields',
        fields: [
            { key: 'shareholders_pep', label: 'Is any of the listed shareholders a Politically Exposed Person (PEP)?', type: 'yesno' },
            { key: 'shareholders_pep_details', label: 'If yes: name of the shareholder and reason for the PEP status', multiline: true },
            { key: 'board_pep', label: 'Is any of the listed board members a Politically Exposed Person (PEP)?', type: 'yesno' },
            { key: 'board_pep_details', label: 'If yes: name of the board member and reason for the PEP status', multiline: true },
        ],
    },
    {
        key: 'sa_board_members',
        title: 'Members of the Board',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Full Name' },
            { key: 'role', label: 'Role / Details' },
            { key: 'nationality', label: 'Nationality' },
            { key: 'dob', label: 'Date of Birth' },
            { key: 'passport_id', label: 'Passport or ID Number' },
            { key: 'phone', label: 'Telephone' },
            { key: 'email', label: 'Email' },
        ],
    },
    {
        key: 'sa_primary_contacts',
        title: 'Primary Contacts',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Full Name' },
            { key: 'role', label: 'Role / Details' },
            { key: 'nationality', label: 'Nationality' },
            { key: 'dob', label: 'Date of Birth' },
            { key: 'phone', label: 'Telephone' },
            { key: 'email', label: 'Email' },
        ],
    },
    {
        key: 'sa_signatories',
        title: 'Authorised Signatories',
        help: 'Specimen signatures: please upload your Signature List in the Documents step.',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Full Name' },
            { key: 'role', label: 'Role / Details' },
            { key: 'nationality', label: 'Nationality' },
            { key: 'phone', label: 'Telephone' },
            { key: 'email', label: 'Email' },
        ],
    },
    {
        key: 'sa_products',
        title: 'Products Traded / Proposed to Trade with IMT',
        type: 'fixed-table',
        rows: [
            { key: 'agri', label: 'Agri' },
            { key: 'coal', label: 'Coal' },
            { key: 'gas', label: 'Gas' },
            { key: 'oil', label: 'Oil' },
            { key: 'ores', label: 'Ores' },
            { key: 'others', label: 'Others (specify)' },
        ],
        columns: [
            { key: 'trades', label: 'Yes / No' },
            { key: 'tonnage', label: 'In Tonnage' },
        ],
    },
    {
        key: 'sa_financials',
        title: 'Audited Financial Information (last 3 years, USD)',
        type: 'table',
        maxRows: 3,
        columns: [
            { key: 'year', label: 'Year' },
            { key: 'current_assets', label: 'Current Assets (USD)' },
            { key: 'current_liabilities', label: 'Current Liabilities (USD)' },
            { key: 'turnover', label: 'Turnover (USD)' },
        ],
    },
    {
        key: 'sa_top_clients',
        title: 'Top 5 Clients',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Client Name' },
            { key: 'country', label: 'Country' },
            { key: 'tonnage_or_pct', label: 'Tonnage or % of Sales' },
        ],
    },
    {
        key: 'sa_top_suppliers',
        title: 'Top 5 Suppliers',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Supplier Name' },
            { key: 'country', label: 'Country' },
            { key: 'tonnage_or_pct', label: 'Tonnage or % of Purchases' },
        ],
    },
    {
        key: 'sa_mine_sites',
        title: 'Mine and Processing Site Data',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'name', label: 'Site Name' },
            { key: 'permit_number', label: 'Permit/Licence No & Authority' },
            { key: 'environmental_licence', label: 'Environmental Licence & Authority' },
            { key: 'export_permit', label: 'Export Permit No' },
            { key: 'country_region', label: 'Country / Region' },
            { key: 'landlord', label: 'Landlord / Plant Owner (if different)' },
        ],
    },
    {
        key: 'sa_stockpile',
        title: 'Stockpile',
        type: 'table',
        maxRows: 5,
        columns: [
            { key: 'location', label: 'Rail Siding / Warehouse / Loadport / Port Allocation' },
            { key: 'distance', label: 'Distance from Mine / Jetty / Production Site' },
            { key: 'overall_capacity', label: 'Overall Capacity' },
            { key: 'secured_capacity', label: 'Counterparty Capacity Secured' },
        ],
    },
    {
        key: 'sa_compliance_ethics',
        title: 'Compliance — Ethical Practices',
        type: 'fields',
        fields: [
            { key: 'code_of_ethics', label: 'Does your company have a written Code of Ethics or Conduct?', multiline: true },
            { key: 'fair_labor', label: 'How does your company ensure fair labor practices and employee welfare?', multiline: true },
            { key: 'harassment_policies', label: 'Are policies in place to prevent workplace harassment and ensure a safe working environment?', multiline: true },
            { key: 'anti_bribery_procedures', label: 'What procedures prevent bribery, corruption, or conflicts of interest?', multiline: true },
            { key: 'abc_training', label: 'Are employees trained on anti-bribery and corruption policies, and how are violations addressed?', multiline: true },
            { key: 'supplier_ethics', label: 'How does your company assess the ethical practices of suppliers and subcontractors?', multiline: true },
        ],
    },
    {
        key: 'sa_compliance_supply_chain',
        title: 'Compliance — Supply Chain',
        type: 'fields',
        fields: [
            { key: 'supplier_criteria', label: 'What criteria are used to evaluate and select suppliers?', multiline: true },
            { key: 'supplier_dd', label: 'Do you conduct due diligence on suppliers for legal, ethical and environmental compliance?', multiline: true },
            { key: 'supplier_coc', label: 'Is there a supplier code of conduct, and how is it enforced?', multiline: true },
            { key: 'labor_monitoring', label: 'Are mechanisms in place to monitor and address labor exploitation in the supply chain?', multiline: true },
            { key: 'supplier_environmental', label: 'Are suppliers required to comply with environmental regulations and sustainable practices?', multiline: true },
            { key: 'conflict_minerals', label: 'Are policies/procedures in place to prevent the use of conflict minerals?', multiline: true },
        ],
    },
    {
        key: 'sa_compliance_environment',
        title: 'Compliance — Environmental',
        type: 'fields',
        fields: [
            { key: 'ems', label: 'Is an environmental management system (EMS) in place?', multiline: true },
            { key: 'ems_monitoring', label: 'How is the EMS implemented and monitored?', multiline: true },
            { key: 'impact_minimisation', label: 'What steps minimise environmental impact?', multiline: true },
            { key: 'past_issues', label: 'Has your company faced environmental compliance issues in the past?', multiline: true },
        ],
    },
    {
        key: 'sa_compliance_data',
        title: 'Compliance — Data Security & Privacy',
        type: 'fields',
        fields: [
            { key: 'data_security', label: 'How is sensitive data protected and data security ensured?', multiline: true },
            { key: 'privacy_notices', label: 'How are individuals informed about collection, use and sharing of their personal data?', multiline: true },
            { key: 'third_parties', label: 'Which third parties receive data, and how is their compliance ensured?', multiline: true },
        ],
    },
    {
        key: 'sa_compliance_monitoring',
        title: 'Compliance — Monitoring & Reporting',
        type: 'fields',
        fields: [
            { key: 'monitoring', label: 'How is compliance with laws, regulations and internal policies monitored?', multiline: true },
            { key: 'audit_frequency', label: 'How frequently are compliance audits conducted, and by whom?', multiline: true },
            { key: 'awareness', label: 'How are employees made aware of compliance requirements and responsibilities?', multiline: true },
            { key: 'violation_handling', label: 'How are compliance violations reported and resolved?', multiline: true },
            { key: 'documentation', label: 'Are documentation practices consistent with legal and regulatory requirements?', multiline: true },
        ],
    },
    {
        key: 'sa_compliance_sanctions',
        title: 'Compliance — Sanction Screening Process',
        type: 'fields',
        fields: [
            { key: 'screening_process', label: 'Is there a documented process for screening partners, customers and transactions against sanction lists?', multiline: true },
            { key: 'screening_tools', label: 'What tools or databases are used for sanction screening?', multiline: true },
            { key: 'screened_lists', label: "Which countries' sanction lists are screened against?", multiline: true },
            { key: 'screening_stages', label: 'Are checks conducted at onboarding, periodic reviews, and before specific transactions?', multiline: true },
            { key: 'list_monitoring', label: 'Are mechanisms in place to monitor sanction-list changes and identify matches promptly?', multiline: true },
            { key: 'escalation', label: 'How are potential matches flagged, reviewed and escalated?', multiline: true },
            { key: 'geopolitical', label: 'Are geopolitical factors and regional sanctions regimes considered?', multiline: true },
            { key: 'high_risk_regions', label: 'Are there specific protocols for entities from high-risk or politically sensitive regions?', multiline: true },
        ],
    },
];

/** Sections for a given case form type ('STANDARD' | 'SA'). */
export const getFormSections = (formType) => (
    formType === 'SA' ? [...KYC_FORM_SECTIONS, ...KYC_SA_SECTIONS] : KYC_FORM_SECTIONS
);

/** Empty form_data skeleton matching the schema. */
export const buildEmptyFormData = (formType) => {
    const data = {};
    getFormSections(formType).forEach((section) => {
        if (section.type === 'table') {
            data[section.key] = [];
            (section.extraFields || []).forEach((field) => {
                data[`${section.key}__extra`] = data[`${section.key}__extra`] || {};
                data[`${section.key}__extra`][field.key] = '';
            });
        } else if (section.type === 'fixed-table') {
            data[section.key] = {};
            section.rows.forEach((row) => {
                data[section.key][row.key] = {};
            });
        } else {
            data[section.key] = {};
            (section.fields || []).forEach((field) => {
                // A multiselect starts as an empty ARRAY, never ''. Every
                // consumer (completeness, the AI merge, the CRM write-back)
                // reads "no boxes ticked" off the list being empty.
                if (field.type === 'multiselect') data[section.key][field.key] = [];
            });
        }
    });
    return data;
};

/**
 * Coerce legacy free-text answers into tickbox lists.
 *
 * Partner Classification was three text boxes until 2026-08. Cases answered
 * before the change hold strings like 'Supplier / Service Provider'. Run this
 * once when a case loads: exact matches auto-tick, and anything we could not
 * place comes back in `legacy` so the partner is shown what still needs
 * picking instead of the answer silently reading as blank.
 */
export const normalizeFormData = (formData, formType) => {
    const data = { ...(formData || {}) };
    const legacy = [];
    getFormSections(formType).forEach((section) => {
        (section.fields || []).forEach((field) => {
            if (field.type !== 'multiselect') return;
            const raw = data[section.key]?.[field.key];
            if (raw === undefined || raw === null) return;
            if (Array.isArray(raw) && raw.every((v) => field.options.includes(v))) return;
            const { matched, unmatched } = coerceToVocabulary(raw, field.options);
            data[section.key] = { ...(data[section.key] || {}), [field.key]: matched };
            if (unmatched.length) {
                legacy.push({
                    section: section.key,
                    field: field.key,
                    label: field.label,
                    unmatched,
                });
            }
        });
    });
    return { formData: data, legacy };
};

/** Count filled vs required fields for a simple completeness indicator. */
export const formCompleteness = (formData, formType) => {
    let required = 0;
    let filled = 0;
    getFormSections(formType).forEach((section) => {
        (section.fields || []).forEach((field) => {
            if (field.required) {
                required += 1;
                const value = formData?.[section.key]?.[field.key];
                // Arrays are explicit: an empty tickbox group is NOT an answer.
                const answered = Array.isArray(value)
                    ? value.length > 0
                    : Boolean(value && String(value).trim());
                if (answered) filled += 1;
            }
        });
    });
    return { required, filled };
};
